import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { StepSection } from '@/components/step-section';
import { TextField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { field_errors_from } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import { use_save_vet_details, use_vet_details } from '@/features/vet/hooks';
import { use_vet_specializations } from '@/features/lookups/hooks';
import {
  MEDICAL_DEGREE_OPTIONS,
  PET_SPECIES_OPTIONS,
  REGISTRATION_NUMBER_REGEX,
  VET_SPECIALIZATION_OPTIONS,
  type MedicalDegree,
  type PetSpecies,
} from '@/features/vet/types';

type Form = {
  vet_full_name: string;
  medical_degree: MedicalDegree | '';
  years_of_experience: string;
  registration_number: string;
  session_fee_inr: string;
  pet_types_treated: PetSpecies[];
  specialization_codes: string[];
};

const EMPTY: Form = {
  vet_full_name: '',
  medical_degree: '',
  years_of_experience: '',
  registration_number: '',
  session_fee_inr: '',
  pet_types_treated: [],
  specialization_codes: [],
};

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/** Keys of `Form` whose value is an array — the multi-select pill fields. */
type ArrayKeys<T> = { [K in keyof T]: T[K] extends unknown[] ? K : never }[keyof T];

export function ModuleVetPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_vet_details({ enabled: !!draft_q.data });
  const save_m = use_save_vet_details();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});
  const [unknown_specs, set_unknown_specs] = useState<string[]>([]);
  // Live lookup table (admin-extendable); hard-coded seed list as fallback.
  const specialization_options = use_vet_specializations(VET_SPECIALIZATION_OPTIONS);

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    if (!draft_q.data.module_types.includes('VET')) {
      navigate(next_module_step_after('VET', draft_q.data.module_types), {
        replace: true,
      });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        vet_full_name: saved_q.data.vet_full_name,
        medical_degree: saved_q.data.medical_degree ?? '',
        years_of_experience: String(saved_q.data.years_of_experience),
        registration_number: saved_q.data.registration_number,
        // session_fee_inr arrives as a string from backend (Prisma Decimal).
        session_fee_inr: saved_q.data.session_fee_inr,
        pet_types_treated: saved_q.data.pet_types_treated,
        specialization_codes: saved_q.data.specialization_codes,
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
  }

  /**
   * Toggle a pill *inside* the updater. Computing `toggle(form.x, v)` at the call
   * site reads the render closure, so several picks landing in one React batch
   * all start from the same stale array and only the last one survives.
   */
  function toggle_in<K extends ArrayKeys<Form>>(key: K, value: Form[K][number]) {
    set_form((f) => ({ ...f, [key]: toggle(f[key] as Form[K][number][], value) }));
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);
    set_unknown_specs([]);

    if (form.vet_full_name.trim().length < 2) {
      set_error('Vet name must be at least 2 characters.');
      return;
    }
    if (form.medical_degree === '') {
      set_error('Select your medical degree.');
      return;
    }
    const yoe = Number(form.years_of_experience);
    if (!Number.isInteger(yoe) || yoe < 0 || yoe > 80) {
      set_error('Years of experience must be a whole number between 0 and 80.');
      return;
    }
    if (
      form.registration_number.trim().length < 5 ||
      form.registration_number.trim().length > 60
    ) {
      set_error('Registration number must be 5–60 characters.');
      return;
    }
    if (!REGISTRATION_NUMBER_REGEX.test(form.registration_number.trim())) {
      set_error('Registration number: alphanumeric, spaces, dashes, slashes only.');
      return;
    }
    const fee = Number(form.session_fee_inr);
    if (!Number.isFinite(fee) || fee <= 0 || fee > 100000) {
      set_error('Session fee must be a positive number ≤ ₹100,000.');
      return;
    }
    if (form.pet_types_treated.length < 1) {
      set_error('Pick at least one species you treat.');
      return;
    }
    if (
      form.specialization_codes.length < 1 ||
      form.specialization_codes.length > 10
    ) {
      set_error('Pick between 1 and 10 specializations.');
      return;
    }

    try {
      await save_m.mutateAsync({
        vet_full_name: form.vet_full_name.trim(),
        medical_degree: form.medical_degree,
        years_of_experience: yoe,
        registration_number: form.registration_number.trim(),
        session_fee_inr: fee,
        pet_types_treated: form.pet_types_treated,
        specialization_codes: form.specialization_codes,
      });
      if (!draft_q.data) return;
      navigate(next_module_step_after('VET', draft_q.data.module_types));
    } catch (err) {
      if (err instanceof ApiError) {
        // Surface unknown specialization codes inline so the tester can
        // deselect them. Backend returns them in details.unknown_specialization_codes.
        const unknown =
          (err.details as { unknown_specialization_codes?: unknown } | undefined)
            ?.unknown_specialization_codes;
        if (Array.isArray(unknown)) {
          set_unknown_specs(unknown.filter((u): u is string => typeof u === 'string'));
        }
        set_field_errors(field_errors_from(err));
        set_error(err.message);
      } else {
        set_error('Could not save vet details');
      }
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-vet"
        title="Veterinary clinic details"
        description="Your credentials, fees, and the cases you handle."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-vet-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <TextField
          label="Vet name"
          name="vet_full_name"
          error={field_errors.vet_full_name}
          data-testid="field-vet-full-name"
          value={form.vet_full_name}
          onChange={(e) => update('vet_full_name', e.target.value)}
          required
          minLength={2}
          maxLength={120}
          placeholder="Dr. Priya Iyer"
        />

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="medical_degree">Medical degree</Label>
            <SelectField
              id="medical_degree"
              data-testid="field-vet-degree"
              value={form.medical_degree}
              onValueChange={(v) => update('medical_degree', v as MedicalDegree | '')}
              placeholder="Select degree"
              options={MEDICAL_DEGREE_OPTIONS}
            />
          </div>
          <TextField
            label="Years of experience"
            name="years_of_experience"
            error={field_errors.years_of_experience}
            data-testid="field-vet-yoe"
            value={form.years_of_experience}
            onChange={(e) =>
              update(
                'years_of_experience',
                e.target.value.replace(/\D/g, '').slice(0, 3),
              )
            }
            inputMode="numeric"
            required
            placeholder="8"
          />
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <TextField
            label="Registration number"
            name="registration_number"
            error={field_errors.registration_number}
            data-testid="field-vet-registration"
            value={form.registration_number}
            onChange={(e) => update('registration_number', e.target.value)}
            required
            minLength={5}
            maxLength={60}
            placeholder="VCI-MH-2018/4521"
            hint="5–60 chars · letters, digits, spaces, dashes, slashes"
          />
          <TextField
            label="Session fee (₹)"
            name="session_fee_inr"
            error={field_errors.session_fee_inr}
            data-testid="field-vet-session-fee"
            value={form.session_fee_inr}
            onChange={(e) =>
              update(
                'session_fee_inr',
                // Allow digits + a single decimal point
                e.target.value.replace(/[^\d.]/g, '').slice(0, 8),
              )
            }
            inputMode="decimal"
            required
            placeholder="750"
            hint="Per consultation. ₹1 – ₹100,000."
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label>Species you treat (pick 1–6)</Label>
          <div className="flex flex-wrap gap-2" data-testid="vet-pet-types">
            {PET_SPECIES_OPTIONS.map((s) => {
              const active = form.pet_types_treated.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`vet-species-${s.value}`}
                  data-checked={active}
                  onClick={() =>
                    toggle_in('pet_types_treated', s.value)
                  }
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input bg-card text-foreground hover:bg-muted')
                  }
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Specializations (pick 1–10)</Label>
          <div className="flex flex-wrap gap-2" data-testid="vet-specializations">
            {specialization_options.map((s) => {
              const active = form.specialization_codes.includes(s.code);
              const flagged = unknown_specs.includes(s.code);
              return (
                <button
                  key={s.code}
                  type="button"
                  data-testid={`vet-spec-${s.code}`}
                  data-checked={active}
                  onClick={() =>
                    toggle_in('specialization_codes', s.code)
                  }
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                    (flagged
                      ? 'border-destructive bg-destructive/10 text-destructive'
                      : active
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-input bg-card text-foreground hover:bg-muted')
                  }
                >
                  {s.display_name}
                </button>
              );
            })}
          </div>
          {unknown_specs.length > 0 && (
            <p className="text-xs text-destructive" data-testid="vet-unknown-specs">
              Server rejected: {unknown_specs.join(', ')}. Deselect them and resubmit.
            </p>
          )}
        </div>
      </StepSection>
    </form>
  );
}
